<template>
  <div class="mcw-tree-contracts">
    <div class="tree-contract-select-wrap">
      <SearchSelect
        class="tree-contract-select"
        :options="contractOptionsForSelect"
        :initial="currentContractOption"
        label="name_contract"
        universal-mode
        :needles="['name_contract', 'N_contract', 'id_contract']"
        :loading="contractsLoading"
        placeholder="Выбрать контракт..."
        title-width="100%"
        @selectItem="selectContractFromDropdown"
      />
    </div>

    <div
      v-if="noMatchCurrentContract || !parsedContracts.length && currentContract"
      class="no-match-current-contract br-25"
    >
      <span class="badge badge-bind" @click="bindContractToNode">
        <i class="material-icons link-icon">commit</i>
        Связать
      </span>
      <span class="contract-name pl-5">
        Текущий контракт не связан с данным подразделением
      </span>
    </div>

    <div
      v-if="matchedContractItem"
      class="already-connected br-25"
      :class="{
        'already-connected_direct': matchedContractState === 'main' || matchedContractState === 'main-exception',
        'already-connected_inherited': matchedContractState === 'connected',
        'already-connected_exception': matchedContractState === 'exception',
      }"
    >
      <i class="material-icons link-icon">check_circle</i>
      <span class="contract-name pl-5">{{ matchedContractText }}</span>
    </div>

    <div
      v-for="item in sortedContracts"
      :key="item.id_contract"
      class="contract-row"
      :class="{ 'contract-row_current': isCurrentSelected(item) }"
    >
      <i
        class="material-icons link-icon"
        :title="connectionTitles(item)"
        @click="onIconClick(item)"
      >
        {{ connectionIcon(item) }}
      </i>

      <div class="contract-main">
        <div class="contract-title">
          <span class="contract-name" :class="textClasses(item)">
            {{ item.name_contract || '— неизвестный контракт —' }}
          </span>

          <span
            v-if="connectionState(item) === 'main-exception'"
            class="badge badge-exception"
          >
            главное исключение
          </span>

          <!-- <span>
            <i
              class="material-icons link-icon"
              title="QR код перехода к ЛК"
              @click="getDataForQR(item)"
            >
              {{ 'qr_code_scanner' }}
            </i>
          </span> -->
        </div>

        <div class="contract-meta">
          <span class="meta">ID: {{ item.id_contract }}</span>
          <span class="meta">№ {{ item.N_contract || '—' }}</span>
          <span class="meta">{{ item.date_contract || '—' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import SearchSelect from './SearchSelect/SearchSelect.vue';

export default {
  name: 'TreeContracts',
  components: {
    SearchSelect,
  },
  props: {
    node: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState({
      contracts: (state) => state.contractsData.contracts,
      contractsLoading: (state) => state.contractsData.processing,
      currentContract: (state) => state.contractsData.currentContract,
      flatTree: (state) => state.treeData.flatTree,
    }),

    currentContractOption() {
      return this.contractOptionsForSelect.find(
        (c) => String(c.id_contract) === String(this.currentContract),
      ) || {};
    },

    contractOptionsForSelect() {
      return this.contracts
        .filter((c) => Number(c.id_contract) !== 0)
        .map((c) => ({
          ...c,
          name_contract: `${c.id_contract} | ${c.name_contract}`,
        }));
    },

    parsedContracts() {
      if (!this.node.contracts_connected) return [];

      let raw;
      try {
        raw = typeof this.node.contracts_connected === 'string'
          ? JSON.parse(this.node.contracts_connected)
          : this.node.contracts_connected;
      } catch (e) {
        return [];
      }

      return raw.map((c) => {
        const contract = this.contracts.find(
          (x) => String(x.id_contract) === String(c.id_contract),
        ) || {};

        return {
          ...c,
          name_contract: contract.name_contract,
          N_contract: contract.N_contract,
          date_contract: contract.date_contract,
        };
      });
    },

    matchedContractItem() {
      if (!this.currentContract) return null;
      return this.parsedContracts.find(
        (item) => String(item.id_contract) === String(this.currentContract),
      ) || null;
    },

    matchedContractState() {
      if (!this.matchedContractItem) return null;
      return this.connectionState(this.matchedContractItem);
    },

    matchedContractText() {
      switch (this.matchedContractState) {
        case 'main':
        case 'main-exception':
          return 'Контракт связан с данным подразделением напрямую';
        case 'exception':
          return 'Контракт исключён из наследования для данного подразделения';
        default:
          return 'Контракт унаследован от вышестоящего подразделения';
      }
    },

    noMatchCurrentContract() {
      return (
        this.currentContract
        && this.parsedContracts.length
        && !this.matchedContractItem
      );
    },

    sortedContracts() {
      return [...this.parsedContracts].sort((a, b) => {
        const weight = (item) => {
          if (item.main === 1) return 0;
          if (item.exception === 0) return 1;
          return 2;
        };

        return weight(a) - weight(b);
      });
    },
  },
  methods: {
    isCurrentSelected(item) {
      return !!(this.currentContract
        && String(item.id_contract) === String(this.currentContract));
    },

    connectionState(item) {
      const isCurrent = this.currentContract
      && String(item.id_contract) === String(this.currentContract);

      if (item.main === 1) {
        if (item.exception_main === 1 && isCurrent) {
          return 'main-exception';
        }
        return 'main';
      }

      if (item.exception === 1) {
        return 'exception';
      }

      return 'connected';
    },

    connectionTitles(item) {
      switch (this.connectionState(item)) {
        case 'main':
          return 'Прямая связь с подразделением. Нажмите, чтобы удалить связь.';
        case 'main-exception':
          return 'Главный контракт-исключение. Нажмите, чтобы удалить связь.';
        case 'exception':
          return 'Контракт исключен. Нажмите, чтобы снова наследовать его.';
        default:
          return 'Наследуемый контракт. Нажмите, чтобы сделать его исключением.';
      }
    },

    connectionIcon(item) {
      switch (this.connectionState(item)) {
        case 'main':
          return 'do_disturb';
        case 'main-exception':
        case 'exception':
          return 'link';
        default:
          return 'link_off';
      }
    },

    textClasses(item) {
      const state = this.connectionState(item);
      return {
        'text-main': state === 'main',
        'text-connected': state === 'connected',
        'text-exception': state === 'exception' || state === 'main-exception',
      };
    },

    onIconClick(item) {
      const state = this.connectionState(item);

      if (state === 'main') {
        this.removeConnection(item);
        return;
      }

      this.toggleException(item);
    },

    getDataForQR(item) {
      this.$emit('get-qr-data', {
        id_contract: item.id_contract,
        id_tree: this.node.id_tree,
      });
    },

    getNodeIdForUnlink(nodeId, contractId) {
      const node = this.flatTree.find((n) => n.id_tree === nodeId);
      if (!node) return null;

      if (node.id_type === 1) {
        return node.id_tree;
      }

      if (node.id_type === 2 && node.id_parent) {
        const parent = this.flatTree.find((n) => n.id_tree === node.id_parent);

        if (parent) {
          let contracts = [];

          try {
            contracts = JSON.parse(parent.contracts_connected || '[]');
          } catch {
            contracts = [];
          }

          const hasContract = contracts.some(
            (c) => c.id_contract === contractId,
          );

          if (hasContract) {
            return parent.id_tree;
          }
        }
      }

      return node.id_tree;
    },

    toggleException(item) {
      this.$emit('toggle-exception', {
        tree: [{
          id_tree: this.node.id_tree,
          exception: item.exception === 1 ? 0 : 1,
        }],
        id_contract: item.id_contract,
        delete: 0,
      });
    },

    removeConnection(item) {
      const targetNode = this.getNodeIdForUnlink(this.node.id_tree, item.id_contract);
      this.$emit('toggle-exception', {
        tree: [{
          id_tree: targetNode,
          exception: item.exception,
        }],
        id_contract: item.id_contract,
        delete: 1,
      });
    },

    async selectContractFromDropdown(option) {
      if (!option || !option.id_contract) return;

      const id = `${option.id_contract}`;

      if (this.currentContract === id) {
        this.$store.commit('contractsData/SET_CURRENT_CONTRACT', '');
        this.$store.commit('contractsData/RESET_CONTRACT_DATA');
        await this.$store.dispatch('employeesData/getLastPatients', { includeServices: false });
        return;
      }

      this.$store.commit('contractsData/SET_CURRENT_CONTRACT', id);
      await this.$store.dispatch('contractsData/getConclusions', id);
    },

    bindContractToNode() {
      let targetNode;
      if (this.node.id_type === 2) {
        targetNode = this.node.id_parent;
      } else {
        targetNode = this.node.id_tree;
      }
      this.$emit('toggle-exception', {
        tree: [{
          id_tree: targetNode,
          exception: 0,
        }],
        id_contract: this.currentContract,
        delete: 0,
      });
    },
  },
};
</script>

<style scoped>
.mcw-tree-contracts {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tree-contract-select-wrap {
  margin-bottom: 4px;
}

:deep(.tree-contract-select .search-select__title) {
  display: flex;
  align-items: center;
  width: 100%;
}

:deep(.tree-contract-select .search-select__selected) {
  flex: 1 1 auto;
  min-width: 0;
  max-width: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.tree-contract-select .search-select__icon) {
  flex: 0 0 auto;
}

.contract-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 6px;
}

.contract-row_current {
  background-color: rgba(33, 150, 243, 0.08);
  outline: 1px solid rgba(33, 150, 243, 0.4);
}

.link-icon {
  font-size: 18px;
  cursor: pointer;
  margin-top: 2px;
}

.link-icon:hover {
  opacity: 0.7;
}

.contract-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}

.contract-title {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: space-between;
  width: 100%;
}

.contract-name {
  font-size: 13px;
}

.text-main {
  font-weight: 600;
  color: #2e7d32;
}

.text-connected {
  font-weight: 600;
  color: #2e537d;
}

.text-exception {
  color: rgba(0, 0, 0, 0.38);
}

.contract-meta {
  display: flex;
  gap: 10px;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.54);
}

.badge {
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 10px;
  line-height: 14px;
}

.badge-exception {
  background: rgba(244, 67, 54, 0.15);
  color: #c62828;
}

.badge-bind {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #2e537d;
  color: #fff;
}

.no-match-current-contract {
  background-color: rgba(255, 179, 80, 0.5);
  padding: 0.25rem;
  display: inline-flex;
  align-items: center;
}

.already-connected {
  padding: 0.25rem 0.5rem;
  display: inline-flex;
  align-items: center;
}

.already-connected_direct {
  background-color: rgba(46, 125, 50, 0.15);
}

.already-connected_direct .link-icon {
  color: #2e7d32;
}

.already-connected_inherited {
  background-color: rgba(46, 83, 125, 0.15);
}

.already-connected_inherited .link-icon {
  color: #2e537d;
}

.already-connected_exception {
  background-color: rgba(198, 40, 40, 0.12);
}

.already-connected_exception .link-icon {
  color: #c62828;
}

.br-25 {
  border-radius: 0.25rem;
}

.pl-5 {
  padding-left: 0.5rem;
}
</style>
